import {
  GEN5_ELBOW_LINKAGE,
  GEN5_ELBOW_STAGE,
  GEN5_KNEE_LINKAGE,
  GEN5_KNEE_STAGE,
  ratioAt,
  ratioSpan,
  type Ratio,
} from './ratio'
import { AXIS_KEYS, type AxisKey, type Rig } from './rig'

/**
 * Ebene 2 — die Baugruppe eines Gelenks.
 *
 * DER ERSTE SCHNITT. Vollständig wird diese Ebene erst mit dem Gelenkdetail:
 * Bauteile im eigenen Rahmen, Hüllkurven, Übersetzung als Kurve über dem
 * Gelenkwinkel, Rückmeldung aus dem PLM. Was hier steht, ist der Teil, den die
 * Übersicht sofort braucht — und er steht bewusst schon in seiner endgültigen
 * Form, damit das Wachsen ein Ergänzen wird und kein Umbauen.
 *
 * WAS SIE JETZT SCHON RICHTIGSTELLT: die Motorzahl ist nicht die Achszahl. Das
 * Knie hat EINE Achse und ZWEI Motoren — zwei rahmenlose Läufer, spiegelbildlich
 * im Oberschenkel, die je eine Hälfte eines gestuften Planetensatzes treiben.
 * Wer Motoren zählt, indem er Achsen zählt, bestellt die Hälfte.
 *
 * Und umgekehrt: das Rumpfgelenk hat ZWEI Achsen und ZWEI Motoren, aber nur EINE
 * Baugruppe — beide Antriebe wirken über Schubstangen auf dieselbe Plattform,
 * und keiner von beiden gehört zu genau einer der beiden Achsen. Eine Zuordnung
 * Motor-zu-Achse gibt es dort nicht.
 */

export type MechanismKind =
  /** Motor sitzt auf der Gelenkachse. */
  | 'direkt'
  /** Planetensatz zwischen Motor und Gelenk, gleiche Achse. */
  | 'planet'
  /** Taumelgetriebe (Zykloid-Wellengetriebe) zwischen Motor und Gelenk — der Gen5-Aktor WG_V12. */
  | 'taumel'
  /** Kurbel und Schubstange auf einen Hebel — die Übersetzung läuft über den Winkel. */
  | 'kurbelschwinge'
  /** Mehrere Antriebe wirken über Stangen auf eine Plattform. Gekoppelte Achsen. */
  | 'parallel'
  | 'riemen'
  | 'kegelrad'
  /**
   * Passiv: eine Feder statt eines Antriebs.
   *
   * Nicht dasselbe wie `keine`. Eine Feder ist ein Bauteil mit einer Kennlinie,
   * sie speichert und gibt zurück, sie hat einen Anschlag und ein zulässiges
   * Moment. Im Gangmodell ist ein gefedertes Gelenk keine Null.
   */
  | 'feder'
  /** Kein Antrieb und nichts, was an seine Stelle träte. */
  | 'keine'

export const MECHANISM_LABELS: Record<MechanismKind, string> = {
  direkt: 'direkt',
  planet: 'Planetensatz',
  taumel: 'Taumelgetriebe',
  kurbelschwinge: 'Kurbelschwinge',
  parallel: 'Parallelkinematik',
  riemen: 'Riemen',
  kegelrad: 'Kegelrad',
  feder: 'Feder, passiv',
  keine: 'ohne Antrieb',
}

export interface Mechanism {
  /** Der Knoten, an dem sie sitzt. */
  node: string
  kind: MechanismKind
  /**
   * Aktiv heisst: mit Motor. Passiv heisst: ohne Motor.
   *
   * Das ist die ganze Unterscheidung. Passiv erzeugt keine Antriebszeile und
   * verbraucht keinen Strom — Masse, Reibung, Anschläge und eine mögliche Feder
   * bleiben, und ein gefedertes Gelenk ist im Gangmodell keine Null.
   */
  driven: boolean
  /**
   * Wie viele Motoren die Baugruppe hat.
   *
   * NICHT ABLEITBAR AUS DER ACHSZAHL, und das ist der Grund, warum diese Zahl
   * hier steht. Zwei Motoren auf einer Achse sind eine Bauentscheidung: sie
   * halbieren das Moment je Läufer und passen so in einen Bauraum, in den ein
   * einzelner nicht passt.
   */
  motors: number
  /** Welche Freiheiten des Knotens sie treibt. */
  drives: AxisKey[]
  /**
   * Die Übersetzung vom Motor zum Gelenk.
   *
   * EINE ZAHL, EINE KURVE ODER EINE MATRIX — je nach Bauform, und nicht immer
   * eine Zahl. Ein Planetensatz hat eine; eine Kurbelschwinge hat eine Kurve
   * über dem Gelenkwinkel; eine Parallelkinematik hat je Achse gar keine, weil
   * dort jeder Motor auf jede Achse wirkt.
   */
  ratio: Ratio
  /** Der Nennwert derselben Übersetzung — bei einer Kurve ihr Mittelwert. */
  nominal: number
  eta: number
}

/**
 * Der heutige Stand — Gen5 V0_24, soweit er belegt ist.
 *
 * Nur die Abweichungen vom Regelfall stehen hier. Der Regelfall ist: ein Motor
 * je Freiheit, direkt. Er gilt für alles, was hier nicht genannt ist — und er
 * gilt als ANNAHME, nicht als Auslegung. Genau deshalb tragen solche Achsen den
 * Reifegrad `entwurf`, bis jemand ihre Baugruppe ausgearbeitet hat.
 *
 * Die Übersetzungen, Kurbeln und Stangen stammen aus dem Kinematikstand
 * (Tabellen „Stangenantriebe" und „Antriebe und Reserven"); die Zahlen am
 * Gelenk stehen in `hs1/motorisation.ts`.
 */
const KNOWN: Record<string, Partial<Mechanism>> = {
  /*
    Das Rumpfgelenk. Zwei Bauchmotoren (FMK07019, i 26) im Oberkörper, Kurbeln
    nach aussen, Stangen nach unten auf den Taillenträger. Beide Motoren wirken
    auf beide Kreuzgelenkwinkel — die Übersetzung ist in Wahrheit eine Matrix
    über der Stellung, und die Zahl hier ist ihr Nennwert in der Nullpose:
    Getriebe 26 mal Stange 1,58 (Nicken) beziehungsweise 1,75 (Rollen), also
    41 und 45.
  */
  cross: {
    kind: 'parallel',
    motors: 2,
    /*
      Summe und Differenz. Beide Motoren zusammen stellen das Nicken, ihr
      Unterschied das Rollen — deshalb steht in jeder Zeile jeder Motor, und
      deshalb ist eine Zuordnung „ein Motor je Achse" hier keine Vereinfachung,
      sondern falsch. Die Einträge sind Nennwerte; in Wahrheit hängen sie an
      der Stellung der Plattform.
    */
    ratio: {
      kind: 'matrix',
      axes: ['y', 'x'],
      rows: [
        [41, 41],
        [45.5, -45.5],
      ],
      nominal: 41,
    },
    eta: 0.87,
  },

  /*
    Das Knie. Zwei FMK08523 im Oberschenkel, Taumelstufe 46,26, gemeinsame
    Kurbel 72,3, Stange 181,1 auf den Hebel 51,6 am Unterschenkel. Die Kette
    läuft von 0,36 gestreckt über 0,71 bei 60–70° auf 0,32 bei 140° — die
    Gesamtübersetzung von 15 bis 33. Die Kurve rechnet das aus den CAD-Punkten
    des Stands.
  */
  knee: {
    kind: 'kurbelschwinge',
    motors: 2,
    ratio: { kind: 'kurve', stage: GEN5_KNEE_STAGE, linkage: GEN5_KNEE_LINKAGE },
    eta: 0.87,
  },

  /* Die Hüfte: drei Achsen in Reihe, je ein Aktor mit Taumelgetriebe. Gieren
     und Nicken i 25, Rollen i 33 (gesetzt; der CAD-Aktor hat 25). */
  hipG: { kind: 'taumel', motors: 1, ratio: { kind: 'zahl', value: 25 }, eta: 0.9 },
  hipR: { kind: 'taumel', motors: 1, ratio: { kind: 'zahl', value: 33 }, eta: 0.9 },
  hipN: { kind: 'taumel', motors: 1, ratio: { kind: 'zahl', value: 25 }, eta: 0.9 },

  /*
    Das Sprunggelenk. Zwei Fussmotoren (FMK07019, i 26) im Unterschenkel,
    Kurbeln 31,7, Stangen auf den Fuss (Hebel 65 mm). Beide Stangen stellen
    beide Kreuzgelenkwinkel: Nicken mit Stange 1,47 je Kurbel (0,5 bei −40,
    2,2 bei +15), Rollen mit 0,90 — also 38 und 23 am Gelenk.
  */
  ankle: {
    kind: 'parallel',
    motors: 2,
    ratio: {
      kind: 'matrix',
      axes: ['y', 'x'],
      rows: [
        [38, 38],
        [23.4, -23.4],
      ],
      nominal: 38,
    },
    eta: 0.87,
  },

  /* Die Schulter: A1 und A2 mit FMK07019 i 36 auf der Achse; A3
     (Oberarm-Drehen) läuft mit FMK06018 i 26 und steht hier mit dem
     Nennwert der beiden Hauptachsen. */
  shoulder: { kind: 'taumel', motors: 3, ratio: { kind: 'zahl', value: 36 }, eta: 0.9 },

  /* Der Ellbogen ist passiv angelenkt: Bizepsmotor (FMK07019, i 50) im
     Oberarm, Kurbel 45, Zugstange 137,8 auf den Hebel 30 am Unterarm. */
  elbow: {
    kind: 'kurbelschwinge',
    motors: 1,
    ratio: { kind: 'kurve', stage: GEN5_ELBOW_STAGE, linkage: GEN5_ELBOW_LINKAGE },
    eta: 0.87,
  },

  /* Unterarm-Drehen: FMK06018 i 10, 55 mm unter dem Ellbogen. */
  wrist: { kind: 'taumel', motors: 1, ratio: { kind: 'zahl', value: 10 }, eta: 0.9 },

  /*
    Das Zehengrundgelenk — PASSIV. Der Gen5-Fuss ist starr und hat die Freiheit
    gar nicht; der Eintrag gilt dem HS-2, bei dem dort eine progressive Feder
    mit Vorspannung und Anschlag sitzt. Die Kennlinie steht in `hs1/toe.ts`.
  */
  mtp: { kind: 'feder', driven: false, ratio: { kind: 'zahl', value: 1 }, eta: 1 },
}

/** Welche Knoten passiv sind — ohne Motor. */
export const passiveNodes = (rig: Rig): Set<string> =>
  new Set(mechanisms(rig).filter((m) => !m.driven).map((m) => m.node))

/**
 * Die Baugruppen zum heutigen Baum.
 *
 * Rein: gleiche Eingaben, gleiches Ergebnis. Ein Knoten ohne Freiheit bekommt
 * keine Baugruppe — es gibt dort nichts anzutreiben.
 */
export function mechanisms(rig: Rig): Mechanism[] {
  const out: Mechanism[] = []
  for (const id of rig.order) {
    const node = rig.byId[id]
    const drives = AXIS_KEYS.filter((a) => node.freedom[a] !== null)
    if (drives.length === 0) continue

    const known = KNOWN[id] ?? {}
    /* Ohne bekannte Bauform: ein Motor je Freiheit, direkt. Eine Annahme, und
       sie ist als solche am Reifegrad des Knotens abzulesen. */
    const driven = known.driven ?? true
    const ratio: Ratio = known.ratio ?? { kind: 'zahl', value: 1 }

    out.push({
      node: id,
      kind: known.kind ?? (driven ? 'direkt' : 'keine'),
      driven,
      motors: driven ? (known.motors ?? drives.length) : 0,
      drives,
      ratio,
      nominal: ratio.kind === 'kurve' ? ratioSpan(ratio).mean : ratioAt(ratio),
      eta: known.eta ?? 0.9,
    })
  }
  return out
}

/**
 * Wie viele Motoren die Maschine hat.
 *
 * `mirrored` zählt DOPPELT — ein Knie gibt es links und rechts, und jedes hat
 * seine zwei Motoren. Eine ausgeschaltete Achse zählt nur mit, wenn ihr Antrieb
 * verbaut bleibt: wer sie samt Antrieb ausbaut, bestellt auch keinen Motor.
 */
export function motorCount(rig: Rig): number {
  let sum = 0
  for (const mech of mechanisms(rig)) {
    const node = rig.byId[mech.node]
    if (node.off && !node.off.drive) continue
    sum += mech.motors * (node.mirrored ? 2 : 1)
  }
  return sum
}

/** Wie viele Baugruppen ausgearbeitet sind und wie viele nur angenommen. */
export function mechanismState(rig: Rig): { known: number; assumed: number } {
  let known = 0
  let assumed = 0
  for (const mech of mechanisms(rig)) {
    if (KNOWN[mech.node]) known++
    else assumed++
  }
  return { known, assumed }
}
